import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { getAllReports } from "../../../redux/service/action";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import FullScreenLoader from "../../Layouts/FullScreenLoader/FullScreenLoader";
import { formatDate, formatDateTime } from "../../Common/Utils/dateUtils";
import {
  formatPurchaseSourceLabel,
  getMembershipStatusClass,
  getMembershipTermLabel,
} from "../../Common/Utils/adminViewUtils";
import TestingAccountFilter from "../../Common/TestingAccountFilter";
import "../../Common/Styles/AdminEnhancements.css";

const INITIAL_PARAMS = {
  type: "payments_report",
  page: 1,
  per_page: 10,
  search_key: "",
  membership_status: "",
  purchase_source: "",
  start_date: "",
  end_date: "",
  testing_account_filter: "hide",
};

const MEMBERSHIP_STATUS_OPTIONS = [
  { value: "", label: "All Membership States" },
  { value: "1", label: "Active" },
  { value: "2", label: "Cancelled / Expired" },
  { value: "0", label: "Pending" },
];

const SOURCE_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "website", label: "Website" },
  { value: "shopify", label: "Shopify" },
  { value: "wordpress", label: "WordPress" },
  { value: "unknown", label: "Unknown" },
];

const formatAmount = (value) => {
  const parsed = Number(value ?? 0);
  if (Number.isNaN(parsed)) return "--";
  return parsed.toFixed(2);
};

const PaymentsReport = () => {
  const navigate = useNavigate();
  const [params, setParams] = useState(INITIAL_PARAMS);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportsData, setReportsData] = useState({
    payments_report: [],
    pagination_meta: null,
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setParams((prev) => ({
        ...prev,
        search_key: searchText,
        page: 1,
      }));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  useEffect(() => {
    let active = true;

    const fetchReport = async () => {
      setLoading(true);
      try {
        const response = await getAllReports(params);
        if (!active) return;

        if (response?.data?.status) {
          const payload = response?.data?.response || {};
          setReportsData({
            payments_report: payload?.payments_report || [],
            pagination_meta: payload?.pagination_meta || null,
          });
        } else {
          setReportsData({
            payments_report: [],
            pagination_meta: null,
          });
        }
      } catch (error) {
        if (active) {
          console.error(error);
          setReportsData({
            payments_report: [],
            pagination_meta: null,
          });
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchReport();
    return () => {
      active = false;
    };
  }, [params]);

  const rows = reportsData?.payments_report || [];

  const summary = useMemo(() => {
    const cancelled = rows.filter((row) => Number(row?.membership_status) === 2).length;
    const active = rows.filter((row) => Number(row?.membership_status) === 1).length;
    const shopify = rows.filter(
      (row) => formatPurchaseSourceLabel(row?.purchase_source_label) === "Shopify"
    ).length;

    return [
      {
        label: "Completed payments",
        value: rows.length,
        subtext: reportsData?.pagination_meta?.total
          ? `${reportsData.pagination_meta.total} total records`
          : "Current result set",
      },
      {
        label: "Active memberships",
        value: active,
        subtext: "Membership currently active",
      },
      {
        label: "Cancelled / expired",
        value: cancelled,
        subtext: "Membership cancellation state",
      },
      {
        label: "Shopify purchases",
        value: shopify,
        subtext: "Payments sourced from Shopify",
      },
    ];
  }, [rows, reportsData]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setParams((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };

  const resetFilters = () => {
    setSearchText("");
    setParams(INITIAL_PARAMS);
  };

  return (
    <Card className="py-3">
      <div className="px-3">
        <div className="admin-toolbar">
          <div>
            <h4 className="mb-1">Payments</h4>
            <div className="admin-toolbar-note">
              Completed membership payments with purchase time, membership period, invoice link, and cancellation visibility.
            </div>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-md-4 form-group">
            <label>Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Company, name, email, mobile, user id, membership id, transaction id"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <div className="col-md-2 form-group">
            <label>Membership Status</label>
            <select
              className="form-control"
              name="membership_status"
              value={params.membership_status}
              onChange={handleFilterChange}
            >
              {MEMBERSHIP_STATUS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2 form-group">
            <label>Source</label>
            <select
              className="form-control"
              name="purchase_source"
              value={params.purchase_source}
              onChange={handleFilterChange}
            >
              {SOURCE_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <TestingAccountFilter
            className="col-md-2 form-group"
            value={params.testing_account_filter}
            onChange={handleFilterChange}
          />
          <div className="col-md-2 form-group d-flex align-items-end">
            <button
              type="button"
              className="submit-btn"
              style={{ width: "100%" }}
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>
          <div className="col-md-2 form-group">
            <label>From Date</label>
            <input
              type="date"
              className="form-control"
              name="start_date"
              value={params.start_date}
              onChange={handleFilterChange}
            />
          </div>
          <div className="col-md-2 form-group">
            <label>To Date</label>
            <input
              type="date"
              className="form-control"
              name="end_date"
              value={params.end_date}
              min={params.start_date || undefined}
              onChange={handleFilterChange}
            />
          </div>
        </div>

        <div className="admin-summary-grid">
          {summary.map((item) => (
            <div className="admin-summary-card" key={item.label}>
              <div className="admin-summary-label">{item.label}</div>
              <div className="admin-summary-value">{item.value}</div>
              <div className="admin-summary-subtext">{item.subtext}</div>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <FullScreenLoader />
      ) : (
        <CardBody className="table-sec px-3">
          <div className="customer360-table-wrap">
            <table className="table border-table-sec">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Purchase Time</th>
                  <th>Customer</th>
                  <th>Membership</th>
                  <th>Source</th>
                  <th>Amount</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Membership Status</th>
                  <th>Invoice</th>
                  <th>Transaction</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((row, index) => {
                    const pageOffset =
                      ((reportsData?.pagination_meta?.current_page || params.page || 1) - 1) *
                      (reportsData?.pagination_meta?.per_page || params.per_page || 10);
                    return (
                      <tr key={row?.user_membership_id || index}>
                        <td>{pageOffset + index + 1}</td>
                        <td>{formatDateTime(row?.purchase_created_on || row?.transaction_created_on)}</td>
                        <td>
                          <div>{row?.company_name || "--"}</div>
                          <div className="admin-inline-note">{row?.customer_name || "--"}</div>
                          <div className="admin-inline-note">User ID: {row?.user_id || "--"}</div>
                        </td>
                        <td>
                          <div>{row?.membership_title || "--"}</div>
                          <div className="admin-inline-note">Membership ID: {row?.membership_id || "--"}</div>
                          <div className="admin-inline-note">Term: {getMembershipTermLabel(row) || "--"}</div>
                        </td>
                        <td>
                          <span className="admin-chip">
                            {formatPurchaseSourceLabel(row?.purchase_source_label)}
                          </span>
                        </td>
                        <td>{formatAmount(row?.amount)}</td>
                        <td>{formatDate(row?.start_date)}</td>
                        <td>{formatDate(row?.end_date)}</td>
                        <td>
                          <span className={`admin-chip admin-chip--${getMembershipStatusClass(row?.membership_status)}`}>
                            {row?.membership_status_label || "--"}
                          </span>
                        </td>
                        <td>
                          {row?.invoice_url ? (
                            <a href={row.invoice_url} target="_blank" rel="noreferrer">
                              Open Invoice
                            </a>
                          ) : (
                            "--"
                          )}
                        </td>
                        <td>
                          <div>{row?.transaction_id || "--"}</div>
                          <div className="admin-inline-note">Invoice ID: {row?.invoice_id || "--"}</div>
                        </td>
                        <td>
                          <div className="d-flex justify-content-center">
                            <button
                              className="border-button"
                              onClick={() => navigate(`/user/update/${row?.user_id}`)}
                            >
                              <span>Update Customer</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center">
                      Completed payments were not found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <CommonPagination
            params={params}
            setParams={setParams}
            pagination_meta={reportsData?.pagination_meta}
          />
        </CardBody>
      )}
    </Card>
  );
};

export default PaymentsReport;
